import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import TextField from 'apollo-react/components/TextField';
import EllipsisHorizontal from 'apollo-react-icons/EllipsisHorizontal';
import Search from 'apollo-react-icons/Search';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from '../../../../custom-hooks/useTranslation';
import {
  setSelectedControlObject,
  updateControlProperties,
} from '../../../actions/report-designer.actions';
import PropertyModal from '../../../components/ControlProperty/PropertyModal';
import ReportModelPickerModal from '../../../components/ReportModelPickerModal';
import { ONE_SIDED_CONDITION_VALUES } from '../../../constants/report-designer.constants';
import {
  getHasValidationErrorByIdAndProp,
  getSelectedControlId,
} from '../../../selectors/report-designer.selectors';
import { isExpressionValue } from '../../../utils/report-designer.utils';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    "& [class*='asteriskLeft']": {
      paddingLeft: 4,
    },
  },
  input: {
    margin: '5px 0px 5px 5px',
    minWidth: 200,
    '& svg': {
      color: '#0768fd',
    },
  },
  disabled: {
    '& div > input': {
      backgroundColor: '#f2f2f2 !important',
      border: '1px solid #d9d9d9 !important',
      borderRadius: '4px !important',
    },
  },
  iconButton: {
    marginTop: 7,
    height: 40,
    width: 35,
    border: '1px solid #d9d9d9',
    borderRadius: '0px 4px 4px 0px',
    borderLeft: 'none',
    backgroundColor: '#fff',
    '&:disabled': {
      borderColor: '#b6b5b5',
    },
  },
});

const ZoomTextFieldCell = ({ row, column }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const selectedControlId = useSelector(getSelectedControlId);
  const hasValidationError = useSelector((state) =>
    getHasValidationErrorByIdAndProp(state, { id: row.controlId, prop: column.accessor })
  );

  const [isReportModelPickerOpen, setReportModelPickerOpen] = useState(false);
  const [isExpressionModalOpen, setExpressionModalOpen] = useState(false);

  const {
    header,
    accessor,
    mappingFields = [],
    required = false,
    supportExpression = false,
  } = column;
  const { controlId, condition } = row;
  const value = row[accessor] || '';
  const isDisabled =
    accessor === 'dataFilterField' && ONE_SIDED_CONDITION_VALUES.includes(condition);

  const onChangeHandler = (selectedValue = {}) => {
    if (!_.isEmpty(selectedValue)) {
      const mappingValues = mappingFields.map(({ propertyName, source }) => ({
        propName: propertyName,
        propValue: selectedValue[source],
      }));

      dispatch(updateControlProperties(controlId, mappingValues));

      setReportModelPickerOpen(false);
    }
  };

  const handleSelectedControlObject = () => {
    if (controlId !== selectedControlId) {
      dispatch(setSelectedControlObject(controlId, false));
    }
  };

  const handleClick = () => {
    setReportModelPickerOpen(true);
    handleSelectedControlObject();
  };

  const valueToDisplay = isExpressionValue(value) ? translateString('Expression') : value;

  return (
    <>
      <div className={classes.root}>
        <TextField
          fullWidth
          required={required && !isDisabled}
          asteriskPosition={required ? 'before' : null}
          className={classNames(classes.input, isDisabled ? classes.disabled : '')}
          value={valueToDisplay ?? ''}
          icon={isDisabled ? null : <Search />}
          iconProps={{ onClick: handleClick }}
          onMouseDown={handleSelectedControlObject}
          error={hasValidationError}
          disabled={isDisabled}
          inputProps={{
            readOnly: true,
            tabIndex: '-1',
          }}
        />
        {supportExpression && (
          <IconButton
            className={classes.iconButton}
            onMouseDown={handleSelectedControlObject}
            onClick={() => setExpressionModalOpen(true)}
          >
            <EllipsisHorizontal />
          </IconButton>
        )}
      </div>
      <ReportModelPickerModal
        open={isReportModelPickerOpen}
        onClose={() => setReportModelPickerOpen(false)}
        onChange={onChangeHandler}
      />
      {isExpressionModalOpen && (
        <PropertyModal
          label={header}
          controlId={controlId}
          name={accessor}
          onClose={() => setExpressionModalOpen(false)}
        />
      )}
    </>
  );
};

export default ZoomTextFieldCell;
